/**
 * ArtifactErrorBoundary
 * Isolates preview-rendering errors so a bad doc_model for one artifact can
 * never crash/freeze the chat or the panel — it shows a fallback instead.
 */

import React from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { createLogger } from '@/utils/logger';

const logger = createLogger('ArtifactErrorBoundary');

interface Props {
  children: React.ReactNode;
  /** Remount the boundary when the active artifact changes so a prior error clears. */
  resetKey?: string | null;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
}

// The boundary must stay a class (only classes catch render errors), so its
// fallback lives in a function component where a hook is legal.
function ArtifactPreviewError() {
  const { t } = useLanguage();
  return <div className="artifact-preview-error">{t('chat.couldnTRenderThisPreview')}</div>;
}

export class ArtifactErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(prev: Props) {
    // New artifact selected → clear any previous render error.
    if (prev.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: unknown) {
    logger.error('Artifact preview render failed', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? <ArtifactPreviewError />
      );
    }
    return this.props.children;
  }
}

export default ArtifactErrorBoundary;
